import React, { useState, RefObject } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Chip } from 'primereact/chip';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import {
  queryUserByEmail,
  deleteUserByEmail,
  QueryUserResult,
  DeleteUserResult,
  DeletionProgress,
} from '../../services/adminService';

interface DangerZonePanelProps {
  toast: RefObject<Toast | null>;
}

type PanelState = 'search' | 'results' | 'deleting' | 'completed';

export const DangerZonePanel: React.FC<DangerZonePanelProps> = ({ toast }) => {
  // State
  const [panelState, setPanelState] = useState<PanelState>('search');
  const [searchEmail, setSearchEmail] = useState('');
  const [queryResult, setQueryResult] = useState<QueryUserResult | null>(null);
  const [deleteResult, setDeleteResult] = useState<DeleteUserResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  // Handlers
  const handleQuery = async () => {
    if (!searchEmail.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Email Required',
        detail: 'Please enter an email address to search',
        life: 3000,
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setQueryResult(null);

    try {
      const result = await queryUserByEmail(searchEmail.trim());
      setQueryResult(result);
      setPanelState('results');
      toast.current?.show({
        severity: 'success',
        summary: 'Query Complete',
        detail: `Found ${result.totalDocuments} documents for ${result.email}`,
        life: 3000,
      });
    } catch (err: any) {
      const message = err.message || 'Failed to query user data';
      setError(message);
      toast.current?.show({
        severity: 'error',
        summary: 'Query Failed',
        detail: message,
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!queryResult) return;

    setShowConfirmDialog(false);
    setConfirmEmail('');
    setPanelState('deleting');
    setIsLoading(true);
    setError(null);

    try {
      const result = await deleteUserByEmail(queryResult.email);
      setDeleteResult(result);
      setPanelState('completed');

      if (result.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Deletion Complete',
          detail: `Successfully deleted ${result.totalDeleted} documents`,
          life: 5000,
        });
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'Deletion Partially Complete',
          detail: `Deleted ${result.totalDeleted} documents, ${result.totalFailed} failed`,
          life: 5000,
        });
      }
    } catch (err: any) {
      const message = err.message || 'Failed to delete user data';
      setError(message);
      setPanelState('results'); // Go back to results so they can retry
      toast.current?.show({
        severity: 'error',
        summary: 'Deletion Failed',
        detail: message,
        life: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setPanelState('search');
    setSearchEmail('');
    setQueryResult(null);
    setDeleteResult(null);
    setError(null);
    setConfirmEmail('');
  };

  const confirmEmailMatches = confirmEmail.toLowerCase().trim() === queryResult?.email.toLowerCase();

  // Render helpers
  const renderSearchState = () => (
    <div className="danger-zone-search">
      <h4>Delete User Account</h4>
      <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
        Enter an email address to search for all associated data across the portal and mobile app.
        This will query both user accounts and agent accounts.
      </p>

      <div className="p-inputgroup" style={{ maxWidth: '500px' }}>
        <InputText
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          placeholder="Enter email address..."
          onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
          disabled={isLoading}
        />
        <Button
          label="Query User Data"
          icon="pi pi-search"
          loading={isLoading}
          onClick={handleQuery}
          severity="danger"
        />
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginTop: '1rem' }} />
      )}
    </div>
  );

  const renderResultsState = () => {
    if (!queryResult) return null;

    return (
      <div className="danger-zone-results">
        <div className="id-summary" style={{ marginBottom: '1.5rem' }}>
          <h4>Found Account(s) for: {queryResult.email}</h4>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {queryResult.userId && (
              <Chip label={`User ID: ${queryResult.userId}`} icon="pi pi-user" />
            )}
            {queryResult.agentId && (
              <Chip label={`Agent ID: ${queryResult.agentId}`} icon="pi pi-briefcase" />
            )}
            {queryResult.authUserId && (
              <Chip label={`Auth ID: ${queryResult.authUserId}`} icon="pi pi-shield" />
            )}
          </div>
          {queryResult.hasAuthOnly && (
            <Message
              severity="info"
              style={{ marginTop: '0.75rem' }}
              text="This is an orphaned Firebase Auth user with no Firestore data. Only the authentication account will be deleted."
            />
          )}
        </div>

        {/* Plaid Items Warning */}
        {queryResult.plaidItems.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h5>
              <i className="pi pi-credit-card" style={{ marginRight: '0.5rem' }}></i>
              Plaid Items ({queryResult.plaidItems.length})
            </h5>
            <Message
              severity="warn"
              style={{ marginBottom: '0.5rem' }}
              text="These Plaid connections will be removed from Plaid first. If any fail, the deletion will stop."
            />
            <DataTable value={queryResult.plaidItems} size="small" stripedRows>
              <Column field="institutionName" header="Institution" />
              <Column
                field="hasAccessToken"
                header="Can Remove from Plaid"
                body={(row) => (
                  <span style={{ color: row.hasAccessToken ? '#27ae60' : '#e74c3c' }}>
                    {row.hasAccessToken ? 'Yes' : 'No (missing token)'}
                  </span>
                )}
              />
            </DataTable>
          </div>
        )}

        <Divider />

        {/* Collection Counts */}
        <h5>Documents to Delete</h5>
        <DataTable
          value={queryResult.collections}
          size="small"
          stripedRows
          emptyMessage="No documents found"
        >
          <Column field="collection" header="Collection" />
          <Column field="count" header="Count" style={{ width: '100px', textAlign: 'center' }} />
          <Column field="queryField" header="Matched By" style={{ width: '150px' }} />
        </DataTable>

        <div
          style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <strong>Total Documents: {queryResult.totalDocuments}</strong>
            {queryResult.plaidItems.length > 0 && (
              <span style={{ marginLeft: '1rem', color: '#6c757d' }}>
                + {queryResult.plaidItems.length} Plaid item(s) to remove
              </span>
            )}
            {queryResult.hasAuthOnly && (
              <span style={{ marginLeft: '1rem', color: '#17a2b8' }}>
                (Auth account only)
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              severity="secondary"
              onClick={handleReset}
            />
            <Button
              label={queryResult.hasAuthOnly ? 'Delete Auth Account' : 'Delete Everything'}
              icon="pi pi-trash"
              severity="danger"
              onClick={() => setShowConfirmDialog(true)}
              disabled={queryResult.totalDocuments === 0 && queryResult.plaidItems.length === 0 && !queryResult.hasAuthOnly}
            />
          </div>
        </div>

        {error && (
          <Message severity="error" text={error} style={{ marginTop: '1rem' }} />
        )}
      </div>
    );
  };

  const renderDeletingState = () => (
    <div className="deletion-progress" style={{ textAlign: 'center', padding: '3rem 0' }}>
      <i
        className="pi pi-spin pi-spinner"
        style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '1rem' }}
      ></i>
      <h4>Deleting User Data...</h4>
      <p style={{ color: '#6c757d' }}>
        This may take a few minutes. Do not close this page.
      </p>
      <ProgressBar mode="indeterminate" style={{ height: '6px', maxWidth: '400px', margin: '1.5rem auto' }} />
      <p style={{ fontSize: '0.9rem', color: '#6c757d' }}>
        Removing Plaid items and deleting documents from all collections...
      </p>
    </div>
  );

  const renderCompletedState = () => {
    if (!deleteResult) return null;

    return (
      <div className="deletion-results">
        <div
          style={{
            textAlign: 'center',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            borderRadius: '8px',
            background: deleteResult.success ? '#d4edda' : '#fff3cd',
            border: `1px solid ${deleteResult.success ? '#c3e6cb' : '#ffeeba'}`,
          }}
        >
          <i
            className={`pi ${deleteResult.success ? 'pi-check-circle' : 'pi-exclamation-triangle'}`}
            style={{
              fontSize: '2.5rem',
              color: deleteResult.success ? '#28a745' : '#ffc107',
              marginBottom: '0.5rem',
            }}
          ></i>
          <h4 style={{ margin: '0.5rem 0', color: deleteResult.success ? '#155724' : '#856404' }}>
            {deleteResult.success ? 'Deletion Complete' : 'Deletion Completed with Issues'}
          </h4>
          <p style={{ margin: 0, color: deleteResult.success ? '#155724' : '#856404' }}>
            {deleteResult.totalDeleted} documents deleted
            {deleteResult.totalFailed > 0 && `, ${deleteResult.totalFailed} failed`}
          </p>
        </div>

        {/* Auth Deletion Result */}
        {deleteResult.authDeletionResult && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h5>Firebase Authentication</h5>
            {deleteResult.authDeletionResult.success ? (
              <Message severity="success" text="Auth account deleted successfully" />
            ) : (
              <Message
                severity="warn"
                text={`Auth deletion failed: ${deleteResult.authDeletionResult.error}. Manual removal required in Firebase Console.`}
              />
            )}
          </div>
        )}

        {/* Plaid Deletion Results */}
        {deleteResult.plaidDeletionResults.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h5>Plaid Items</h5>
            <DataTable value={deleteResult.plaidDeletionResults} size="small" stripedRows>
              <Column field="itemId" header="Item ID" />
              <Column
                field="success"
                header="Status"
                body={(row) => (
                  <span style={{ color: row.success ? '#27ae60' : '#e74c3c' }}>
                    {row.success ? 'Removed from Plaid' : `Failed: ${row.error}`}
                  </span>
                )}
              />
            </DataTable>
          </div>
        )}

        <Divider />

        {/* Deletion Results by Collection */}
        <h5>Deletion Results by Collection</h5>
        <DataTable
          value={deleteResult.deletionResults.filter((r) => r.success > 0 || r.failed > 0)}
          size="small"
          stripedRows
          emptyMessage="No deletions performed"
        >
          <Column field="collection" header="Collection" />
          <Column
            field="success"
            header="Deleted"
            style={{ width: '100px', textAlign: 'center' }}
            body={(row: DeletionProgress) => (
              <span style={{ color: '#27ae60' }}>{row.success}</span>
            )}
          />
          <Column
            field="failed"
            header="Failed"
            style={{ width: '100px', textAlign: 'center' }}
            body={(row: DeletionProgress) => (
              <span style={{ color: row.failed > 0 ? '#e74c3c' : '#6c757d' }}>
                {row.failed}
              </span>
            )}
          />
          <Column
            field="error"
            header="Error"
            body={(row: DeletionProgress) =>
              row.error ? (
                <span style={{ color: '#e74c3c', fontSize: '0.85rem' }}>{row.error}</span>
              ) : (
                '-'
              )
            }
          />
        </DataTable>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Button
            label="Delete Another User"
            icon="pi pi-refresh"
            onClick={handleReset}
            severity="secondary"
          />
        </div>
      </div>
    );
  };

  // Main render
  return (
    <Card className="danger-zone-card">
      <div className="danger-zone-header">
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: '2rem', color: '#e74c3c' }}
        ></i>
        <div>
          <h3 style={{ margin: 0, color: '#e74c3c' }}>DANGER ZONE</h3>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d' }}>
            Permanently delete user accounts and all associated data. This action cannot be undone.
          </p>
        </div>
      </div>

      <Divider />

      {panelState === 'search' && renderSearchState()}
      {panelState === 'results' && renderResultsState()}
      {panelState === 'deleting' && renderDeletingState()}
      {panelState === 'completed' && renderCompletedState()}

      {/* Confirmation Dialog */}
      <Dialog
        visible={showConfirmDialog}
        onHide={() => {
          setShowConfirmDialog(false);
          setConfirmEmail('');
        }}
        header="Confirm Permanent Deletion"
        style={{ width: '500px' }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowConfirmDialog(false);
                setConfirmEmail('');
              }}
              severity="secondary"
            />
            <Button
              label={queryResult?.hasAuthOnly ? 'Delete Auth Account' : 'Delete Everything'}
              icon="pi pi-trash"
              onClick={handleDelete}
              severity="danger"
              disabled={!confirmEmailMatches}
            />
          </div>
        }
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '1rem' }}
          ></i>
          <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            This will permanently delete:
          </p>
          <ul style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
            {!queryResult?.hasAuthOnly && (
              <li>{queryResult?.totalDocuments || 0} Firestore documents</li>
            )}
            {(queryResult?.plaidItems.length ?? 0) > 0 && (
              <li>{queryResult?.plaidItems.length} Plaid connection(s)</li>
            )}
            <li>Firebase Authentication account</li>
            {queryResult?.hasAuthOnly && (
              <li style={{ color: '#17a2b8' }}>(No Firestore data found)</li>
            )}
          </ul>

          <p style={{ marginBottom: '0.5rem' }}>
            Type <strong>{queryResult?.email}</strong> to confirm:
          </p>
          <InputText
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="Enter email to confirm"
            style={{ width: '100%' }}
            autoFocus
          />
        </div>
      </Dialog>
    </Card>
  );
};

export default DangerZonePanel;
